import {Colors, IconButton, P, S1, ToolTip, Icon, Panel, Popup, PdfModal, Row, Button, SplitButton, Stack} from 'src/components/UI'
import React, {useEffect, useMemo, useRef, useReducer, Fragment} from 'react'
import Reflux from 'reflux-react-16'
import useCustomerStore from 'src/stores/CustomerStoreZS'

import {Customer, Order, OrderAddress, Reseller, Address} from 'types/types'
import moment from 'moment'
import Table, {TableColumn, TableRef} from '@components/UI/table/index.js'
import _ from 'lodash'
import Languages from '@utils/Languages'
import ViewOrderModal, {ViewOrderModalCustomerRef} from '@components/orders/ViewOrderModalCustomer.js'
import SearchBar from '@components/SearchBar/index.js'
import EditOrderModalCustomer from '@components/orders/EditOrderModalCustomer.js'
import {emit} from 'src/socketio/socketEmit'
import NewOrder from 'server/functions/orders/NewOrder'
import CancelOrderPopup from '@components/orders/CancelOrderPopup'
import AddressStore from 'src/stores/AddressStore'
import dbCodes from 'server/dbCodes'
import {copyColli} from '@utils/copyColli'
import {getListAddresses, getOriginalOrderAddresses} from '@utils/retourAddresses'
import AddressTypeIcon from '@components/orders/AddressTypeIcon.js'
import {formSpacing} from '../../components/UI/forms/index.js'
import type {PopupRef} from '../../components/UI/overlays/Popup.js'
import type {PdfModalRef} from '../../components/UI/overlays/PdfModal.js'
import {printLabels} from '../../components/labels/printLabels.js'
import {orderTrackTraceLink} from '../../../utils/trackTraceLink.js'

interface OrdersProps {
    reseller: Reseller
    user: Customer
    addressBook: Address[]
}

interface OrdersState {
    printerPageSize: string
}

function Orders({reseller, user, addressBook}: OrdersProps): React.ReactNode {
    const {orders, filteredOrders, selectedOrders, ordersLoading, orderQueries, orderStartDate, orderEndDate} = useCustomerStore((state) => state)

    const initialState: OrdersState = {
        printerPageSize: localStorage.getItem('printerPageSize') ?? 'A6'
    }

    const [state, setState] = useReducer((state: OrdersState, newState: Partial<OrdersState>) => ({...state, ...newState}), structuredClone(initialState))

    const tableRef = useRef<TableRef>(null)
    const viewOrderModalRef = useRef<ViewOrderModalCustomerRef>(null)
    const editOrderModalRef = useRef<EditOrderModalCustomer>(null)
    const printPopupRef = useRef<PopupRef>(null)
    const pdfModalRef = useRef<PdfModalRef>(null)
    const cancelOrderPopupRef = useRef<CancelOrderPopup>(null)
    const popupRef = useRef<PopupRef>(null)

    useEffect(() => {
        useCustomerStore.getState().getOrders()
    }, [])

    const columns = useMemo((): TableColumn<Order>[] => {
        return [
            {id: 'date', title: 'Datum', visible: true, key: 'date', width: 120, text: (order: Order) => {
                return moment(order.date).format('DD-MM-YYYY')
            }, sort: (order: Order) => {
                return order.date
            }},
            {id: 'startTime', title: 'Tijdvak', key: 'startTime', visible: true, width: 90, sort: (order, option) => {
                // Without a choice: the start time of the first address
                if (option === 'startTime' || option === 'endTime') {
                    let time = '24:00'

                    order.addresses.forEach((address) => {
                        if (address[option] < time) {
                            time = address[option]
                        }
                    })

                    return time
                }

                return order.addresses[0].startTime
            },
            dropdown: {
                legacyKey: 'timeSortOption',
                options: [
                    {
                        label: 'Starttijd',
                        value: 'startTime'
                    },
                    {
                        label: 'Eindtijd',
                        value: 'endTime'
                    }
                ]
            },
            render: (order) => {
                const {firstAddress, lastAddress} = getListAddresses(order)

                return (
                    <div style={{width: '100%'}}>
                        {firstAddress &&
                            <div style={{display: 'flex', alignItems: 'center', height: 27.5}}>
                                <P ellipsis style={{marginRight: 12}}>{`${firstAddress.startTime} - ${firstAddress.endTime}`.trim()}</P>
                            </div>
                        }

                        {lastAddress &&
                            <div style={{display: 'flex', alignItems: 'center', height: 27.5}}>
                                <P ellipsis style={{marginRight: 12}}>{`${lastAddress.startTime} - ${lastAddress.endTime}`.trim()}</P>
                            </div>
                        }
                    </div>
                )
            }},
            {id: 'address', title: 'Adres', key: 'address', visible: true, minWidth: 250, sort: (order, option) => {
                return order.addresses[0][option]
            }, dropdown: {
                legacyKey: 'addressSortOption',
                defaultValue: 'postalCode',
                options: [
                    {
                        label: 'Naam',
                        value: 'name'
                    },
                    {
                        label: 'Straat',
                        value: 'street'
                    },
                    {
                        label: 'Huisnummer',
                        value: 'nr'
                    },
                    {
                        label: 'Postcode',
                        value: 'postalCode'
                    },
                    {
                        label: 'Plaats',
                        value: 'city'
                    },
                    {
                        label: 'Stopnummer',
                        value: 'stopNumber'
                    }
                ]
            }, flex: 2, render: (order) => {
                const {firstAddress, lastAddress, firstIsOriginal, retourAddress, nrOfOtherAddresses} = getListAddresses(order)

                return (
                    <div style={{width: '100%'}}>
                        {firstAddress &&
                            <div style={{display: 'flex', alignItems: 'center', opacity: firstIsOriginal ? 0.6 : 1}}>
                                <AddressTypeIcon address={firstAddress} isRetour={firstAddress === retourAddress} disabled={firstIsOriginal} />

                                {!firstAddress.position && !firstIsOriginal &&
                                    <ToolTip text='Adres niet gevonden. Controleer de gegevens voor je bevestigt.'>
                                        <Icon size='md' style={{marginRight: 6, color: Colors.errorBright}} icon='mdi mdi-alert' />
                                    </ToolTip>
                                }

                                <P ellipsis style={{width: 150, marginRight: 6}}>
                                    {firstAddress.name}
                                </P>

                                <S1 ellipsis style={{marginRight: 6}}>
                                    {!firstIsOriginal && firstAddress.stopNumber}
                                </S1>

                                <P ellipsis style={{flex: 1}}>
                                    {`${firstAddress.street} ${firstAddress.nr}${firstAddress.addition} ${firstAddress.city}`.trim()}
                                </P>

                                {nrOfOtherAddresses > 0 &&
                                    <S1 style={{marginRight: 15, whiteSpace: 'nowrap'}}>+ {nrOfOtherAddresses}</S1>
                                }
                            </div>
                        }

                        {lastAddress &&
                            <div style={{display: 'flex', alignItems: 'center'}}>
                                <AddressTypeIcon address={lastAddress} isRetour={lastAddress === retourAddress} />

                                {(lastAddress === retourAddress ? !lastAddress.position : (_.some(order.addresses.slice(1), (address) => !address.position) || !lastAddress.position)) &&
                                    <Icon size='md' style={{marginRight: 6, color: Colors.errorBright}} icon='mdi mdi-alert' />
                                }

                                <P ellipsis style={{width: 150, marginRight: 6}}>
                                    {lastAddress.name}
                                </P>

                                <S1 ellipsis style={{marginRight: 6}}>
                                    {lastAddress.stopNumber}
                                </S1>

                                <P ellipsis style={{flex: 1}}>
                                    {`${lastAddress.street} ${lastAddress.nr}${lastAddress.addition} ${lastAddress.city}`.trim()}
                                </P>
                            </div>
                        }
                    </div>
                )
            }},
            {id: 'options', title: 'Adres opties', key: 'options', visible: false, render: (order) => {
                const options = []
                const processedCustomOptions = []

                order.addresses.forEach((address) => {
                    Object.entries(dbCodes.pickupAndDeliveryOptions).forEach(([key, value]) => {
                        if (!options.includes(value)) {
                            if (address[key]) {
                                options.push({type: 'standard', value})
                            }
                        }
                    })

                    if (address.minimumAge) {
                        if (address.minimumAge === 16) {
                            options.push({type: 'standard', value: dbCodes.pickupAndDeliveryOptions['minimumAge +16']})
                        } else if (address.minimumAge === 18) {
                            options.push({type: 'standard', value: dbCodes.pickupAndDeliveryOptions['minimumAge +18']})
                        } else if (address.minimumAge === 21) {
                            options.push({type: 'standard', value: dbCodes.pickupAndDeliveryOptions['minimumAge +21']})
                        }
                    }

                    if (address.customOptions) {
                        const customOptions = reseller.settings.orders.customOptions?.filter((option) => address.customOptions.includes(option.id)) || []
                        customOptions.forEach((customOption) => {
                            if (!processedCustomOptions.includes(customOption.id)) {
                                options.push({
                                    type: 'custom',
                                    value: customOption.name,
                                    color: customOption.color,
                                    id: customOption.id
                                })
                                processedCustomOptions.push(customOption.id)
                            }
                        })
                    }
                })

                return (
                    <P ellipsis>
                        {options.map((option, idx) => {
                            const isLast = idx === options.length - 1
                            if (option.type === 'standard') {
                                return (
                                    <span key={option.value}>
                                        {option.value}{!isLast ? ', ' : ''}
                                    </span>
                                )
                            }

                            return (
                                <Fragment key={option.id}>
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            background: option.color,
                                            color: Colors.isDarkBackground(option.color) ? Colors.textLight : Colors.textDark,
                                            borderRadius: 24,
                                            padding: '0 8px',
                                            marginRight: isLast ? 0 : 4,
                                            fontSize: 12,
                                            lineHeight: '20px',
                                            verticalAlign: 'middle',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {option.value}
                                    </span>
                                </Fragment>
                            )
                        })}
                    </P>
                )
            }},
            {id: 'colli', title: 'Aantal colli', visible: false, key: 'colli', width: 200, text: (order: Order) => {
                return `${order.colli.length || 0}`
            }},
            {id: 'notes', title: 'Opmerkingen', visible: true, key: 'notes', flex: 1},
            {id: 'reference', title: 'Referentie', visible: true, key: 'reference', flex: 1},
            {id: 'trackTrace', title: 'Track & Trace', visible: true, width: 200, key: 'trackTrace', onClick: (order: Order) => {
                if (order.noTrackTrace) {
                    return
                }

                window.open(orderTrackTraceLink(order))
            }},
            {id: 'status', title: 'Status', visible: true, key: 'status', width: 200, render: (order: Order) => {
                const {statusColor, status, subStatus} = Colors.orderStatusColor(order)

                return (
                    <div style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 5, minWidth: 0, background: statusColor, borderRadius: 4}}>
                        <P>{subStatus || status}</P>
                    </div>
                )
            }, sort: (order: Order) => {
                const {subStatus} = Colors.orderStatusColor(order)

                return `${order.status} ${subStatus || ''}`
            }},
            {id: 'eta', title: 'ETA', visible: false, width: 110, text: (order: Order) => {
                const nextAddress = order.addresses.find((address) => !address?.timeOfArrival && address.estimatedTimeOfArrival)

                return nextAddress?.estimatedTimeOfArrival || ''
            }},
            {id: 'pickedUpTime', title: 'Opgehaald om', visible: false, width: 110, text: (order: Order) => {
                const pickupAddress = order.addresses.find((address) => address.type === 'pickup')

                return pickupAddress?.timeOfArrival || ''
            }},
            {id: 'deliveredTime', title: 'Bezorgd om', visible: false, width: 110, text: (order: Order) => {
                const deliveryAddress = order.addresses.findLast((address) => address.type === 'delivery')

                return deliveryAddress?.timeOfArrival || ''
            }},
            {id: 'view', visible: true, width: 42, showOnHover: true, render: (order: Order) => {
                return (
                    <IconButton onClick={() => viewOrderModalRef.current.open(order._id)}>
                        <i className='mdi mdi-eye' />
                    </IconButton>
                )
            }}
        ]
    }, [reseller])

    const onClickEdit = (order: Order) => {
        editOrderModalRef.current.open(order)
    }

    const onClickCopy = async (id?: string) => {
        id = id || selectedOrders[0]

        try {
            const {data: order} = await emit<Order>('orders.getOne', id)

            const newOrder = NewOrder(reseller, {selectedDate: orderStartDate})

            newOrder.addresses = []

            // A retour is copied as the original order, not as an order to the retour address
            getOriginalOrderAddresses(order).map((address) => {
                const newAddress: OrderAddress = {
                    type: address.type,
                    name: address.name,
                    attention: address.attention,
                    street: address.street,
                    nr: address.nr,
                    addition: address.addition,
                    street2: address.street2,
                    postalCode: address.postalCode,
                    city: address.city,
                    country: address.country,
                    email: address.email || '',
                    phone: address.phone || '',
                    instructions: address.instructions,
                    startTime: address.startTime,
                    endTime: address.endTime,
                    isBusiness: address.isBusiness || false,

                    gpsRequired: !!address.gpsRequired,
                    signatureRequired: !!address.signatureRequired,
                    nameRequired: !!address.nameRequired,
                    signatureNeighbors: !!address.signatureNeighbors,
                    scanRequired: !!address.scanRequired,
                    imageRequired: !!address.imageRequired,
                    statedAddressOnly: !!address.statedAddressOnly,
                    mailboxDelivery: !!address.mailboxDelivery,
                    refrigerated: !!address.refrigerated,
                    idCheck: !!address.idCheck,
                    minimumAge: address.minimumAge ?? undefined,
                    customOptions: address.customOptions || [],
                    attachments: address.attachments || []
                }

                if (address.position) {
                    newAddress.position = JSON.parse(JSON.stringify(address.position))
                }

                newOrder.addresses.push(newAddress)
            })

            if (order.colli) {
                const {colli, trackTrace} = copyColli(order.colli, order)

                newOrder.colli = colli

                if (trackTrace) {
                    newOrder.trackTrace = trackTrace
                }
            }

            newOrder.distance = order.distance || 0
            newOrder.priceTableId = order.priceTableId || ''
            newOrder.price = order.price
            newOrder.reference = order.reference
            newOrder.notes = order.notes
            newOrder.autoRetour = !!order.autoRetour
            newOrder.nrOfDeliveries = order.nrOfDeliveries
            newOrder.nrOfDaysBeforeRetour = order.nrOfDaysBeforeRetour

            if (order.retourAddress) {
                newOrder.retourAddress = order.retourAddress
                newOrder.retourAddress = {
                    instructions: '',
                    startTime: '00:00',
                    endTime: '23:59',
                    ...order.retourAddress
                }
            }

            newOrder.allowAddCollo = order.allowAddCollo || 'yes'

            editOrderModalRef.current.open(newOrder)
            useCustomerStore.getState().setSelectedOrders([])
        } catch (err) {
            popupRef.current.open('Error', err.message)
        }
    }

    const onClickPrintLabels = (id?: string, addressIndex?: number, colloIndex?: number) => {
        const ids = id ? [id] : selectedOrders

        printLabels({
            ids,
            printerPageSize: state.printerPageSize,
            printPopup: printPopupRef.current,
            pdfModal: pdfModalRef.current,
            print: async (ids, nrOfEmptyLabels) => {
                try {
                    const {data: pdf, errors} = await emit<string>('orders.printLabels', ids, state.printerPageSize, nrOfEmptyLabels, addressIndex, colloIndex)

                    return {pdf, errors}
                } catch (err) {
                    console.log(err)
                    throw err.message
                }
            },
            itemName: 'ritten',
            onPrint: () => useCustomerStore.getState().setSelectedOrders([])
        })
    }

    const onClickCancel = async (id?: string) => {
        id = id || selectedOrders[0]

        if (id) {
            const order = _.find(orders, (order) => order._id === id)

            if (order) {
                cancelOrderPopupRef.current.open(order)
            }
        } else {
            cancelOrderPopupRef.current.open(selectedOrders)
        }
    }

    const onClickRemove = async (id?: string) => {
        const ids = id ? [id] : selectedOrders

        try {
            if (!await popupRef.current.open('Ritten verwijderen', 'Weet je zeker dat je deze ritten wilt verwijderen?')) return
            const {errors} = await emit('orders.remove', ids)

            if (errors?.length) {
                popupRef.current.setErrors(errors)
            } else {
                popupRef.current.close()
                viewOrderModalRef.current.close()
            }
        } catch (err) {
            popupRef.current.setError(err.message)
        }
    }

    return (
        <Stack style={{flex: 1}}>
            <Panel style={{display: 'flex', borderTop: 'none', background: Colors.backgroundNeutral, padding: '20px 10px 10px 10px'}}>
                <SearchBar
                    columns={columns}
                    onQueryChange={useCustomerStore((state) => state.setOrderQueries)}
                    onDateChange={useCustomerStore((state) => state.setOrderDates)}
                    startDate={orderStartDate}
                    endDate={orderEndDate}
                    queries={orderQueries}
                />
                <Row style={{flexWrap: 'wrap', columnGap: formSpacing.columnGap}}>
                    {user.permissions.orders &&
                        <Row style={{paddingLeft: 6, paddingRight: 6, flex: 0, marginBottom: 6, minWidth: 'auto'}}>
                            <Button
                                style={{minWidth: 115}}
                                variant='outline-white'
                                label={Languages.newOrder['NL']}
                                onClick={() => editOrderModalRef.current.open()}
                            />
                        </Row>
                    }

                    {selectedOrders.length > 0 &&
                        <>
                            <Row style={{paddingLeft: 6, paddingRight: 6, flex: 0, height: 36, marginBottom: 6, columnGap: formSpacing.columnGap, minWidth: 'auto'}}>
                                {selectedOrders.length === 1 &&
                                    <>
                                        {user.permissions.orders &&
                                            <Button
                                                variant='outline-white'
                                                onClick={() => onClickCopy(selectedOrders[0])}
                                                tooltip='Rit kopiëren'
                                            >
                                                <i className='mdi mdi-content-copy' />
                                            </Button>
                                        }
                                    </>
                                }

                                <SplitButton
                                    variant='outline-white'
                                    value={state.printerPageSize}
                                    options={[
                                        {title: 'Papierformaat A6', value: 'A6'},
                                        {title: 'Papierformaat 10x21', value: '10x21'},
                                        {title: 'Papierformaat A5', value: 'A5'},
                                        {title: 'Papierformaat A4 | 4x A6', value: 'A4'},
                                        {title: 'Papierformaat A4 | 3x 10x21', value: 'A410x21'},
                                        {title: 'Papierformaat A4 | 2x A5', value: 'A4A5'}
                                    ]}
                                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                        setState({printerPageSize: event.target.value})
                                        localStorage.setItem('printerPageSize', event.target.value)
                                    }}
                                    onClick={() => onClickPrintLabels()}
                                    tooltip='Labels printen'
                                >
                                    <i className='mdi mdi-printer' />
                                </SplitButton>

                                {user.permissions.orders &&
                                    <Button
                                        variant='outline-white'
                                        tooltip='Ritten annuleren'
                                        onClick={() => onClickCancel()}
                                    >
                                        <i className='mdi mdi-close-circle' />
                                    </Button>
                                }

                                {user.permissions.orders &&
                                    <Button
                                        variant='outline-white'
                                        tooltip='Ritten verwijderen'
                                        icon='mdi mdi-delete'
                                        onClick={() => onClickRemove()}
                                    />}
                            </Row>
                        </>
                    }
                </Row>
            </Panel>

            <div style={{flex: 1, marginTop: 24, marginLeft: 24, marginRight: 24}}>
                <Table
                    tableName='customerOrders'
                    legacyTableName='dashboard'
                    columns={columns}
                    rows={Object.values(filteredOrders)}
                    selectedRows={selectedOrders}
                    loading={ordersLoading}
                    onChange={(selectedRows) => useCustomerStore.getState().setSelectedOrders(selectedRows)}
                    ref={tableRef}
                />
            </div>

            <ViewOrderModal
                reseller={reseller}
                user={user}
                history={history}
                addressBook={[]}
                onClickEdit={onClickEdit}
                onClickCopy={onClickCopy}
                onClickCancel={onClickCancel}
                onClickRemove={onClickRemove}
                onClickPrintLabels={onClickPrintLabels}
                ref={viewOrderModalRef}
            />

            <EditOrderModalCustomer
                reseller={reseller}
                customer={user}
                addressBook={addressBook}
                viewOrder={(id) => viewOrderModalRef.current.open(id)}
                ref={editOrderModalRef}
                planningDate={orderStartDate}
            />

            <Popup ref={popupRef} />
            <Popup ref={printPopupRef} />
            <PdfModal ref={pdfModalRef} />
            <CancelOrderPopup reseller={reseller} ref={cancelOrderPopupRef} />

        </Stack>
    )
}

export default class OrdersWrapper extends Reflux.Component {
    constructor(props) {
        super(props)

        // @ts-expect-error reflux-react-16 is not typed
        this.store = AddressStore
        // @ts-expect-error reflux-react-16 is not typed
        this.state = undefined
        // @ts-expect-error reflux-react-16 is not typed
        this.props = props
    }

    render() {
        return (
            // @ts-expect-error reflux-react-16 is not typed
            <Orders {...this.props} {...this.state} />
        )
    }
}
